import { Router } from '@angular/router';
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { CartUtils } from '../../utils/cart.utils';
import { debounceTime, skipWhile, take, takeUntil, tap } from 'rxjs/operators';
import { MarketingFacade } from '../../facades/marketing.facade';
import { combineLatest, Subject } from 'rxjs';
import { ICart, ICartItemWithDetail, ICartUpdateRequestAttributes } from '../../models/cart.models';
import { AnalyticsService } from '../../analytics/analytics.service';
import { IconType } from '../../models/settings.model';
import { AppActions } from '../../actions';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { IBaseModel, ISystemDetails } from '../../models/common.models';
import { CatalogFacade } from '../../facades/catalog.facade';
import { EFeatureToggles, EInstalledBaseTabs } from '../../configurations/common';
import { CustomerFacade } from '../../facades/customer.facade';
import { ShsEquipmentFacade } from '../../facades/shs-equipment.facade';
import { EquipmentUtils } from '../../utils/equipment.utils';
import { AppUtils } from '../../utils/app.utils';

export interface IAddItemModalProductData {
  id?: string;
  sapP40MaterialPricingGroup3?: string;
  productWebshopCategory?: string;
  [key: string]: unknown;
}

export interface IAddItemModalItem {
  data: IAddItemModalProductData;
  isConcrete: boolean;
  quantity: number;
}

export interface ICartModalItemDetail {
  details: ICartItemWithDetail | undefined;
  quantity: number | undefined;
}

export interface ICartWithItems extends ICart {
  items: ICartModalItemDetail[];
  hasConfigurableItems: boolean;
}

export enum ModalOptions {
  ExistingCart = 'existing-cart',
  NewCart      = 'new-cart',
}

@Component({
  selector: 'app-add-item-modal',
  templateUrl: './add-item-modal.component.html',
  styleUrls: ['./add-item-modal.component.scss'],
  standalone: false,
})
export class AddItemModalComponent implements OnDestroy, OnChanges, OnInit {
  @Input() items: IAddItemModalItem[] = [];
  @Input() currentCartId: string = '';
  @Input() showModal: boolean = false;
  @Input() isModalInitialized: boolean = true;
  @Input() isReorderWorkflowSelected: boolean = false;
  @Input() flNumberOfReorder: string = '';
  @Input() purchaseActivityTab: string = '';
  @Output() isModalInProgress: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();
  @Input() isSapP40Enable: boolean = false;
  @Input() orderId: string = '';

  iconType: typeof IconType = IconType;
  carts: ICartWithItems[] = [];
  isLoadingCarts: boolean = true;

  tabsOpened: string[] = [];
  cartSelected: ICart | undefined = undefined;
  cartWasSelectedInThisModal: boolean = false;
  cartName: (cart: ICart) => string = CartUtils.getCartName;

  isAddToNewCartBtn: boolean = false;
  isAddToExistingCartBtn: boolean = false;
  isAddToCartInProgress: boolean = false;
  showModalSelectEquipment: boolean = false;
  systemDetailsOfReorder: ISystemDetails | undefined = undefined;
  isBusinessPartnerRole: boolean = false;
  pricingGroupForConsumables: string = '';
  radioInputReset: string | null = null;
  isSapStore: boolean = false;
  isNonEquipmentPurchaseEnabled: boolean = false;
  supportedCategoriesForNonEquipmentPurchase: string[] = [];

  private readonly unsubscribe$: Subject<void> = new Subject<void>();

  private readonly router: Router = inject(Router);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly catalogFacade: CatalogFacade = inject(CatalogFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly shsEquipmentFacade: ShsEquipmentFacade = inject(ShsEquipmentFacade);

  ngOnInit(): void {
    this.isSapStore = AppUtils.isSapStore();
    this._isNonEquipmentPurchaseEnabled();
    this.selectCartsWithItems();
    this.selectIsBPRole();
    this.selectPricingGroupForConsumables();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges() {
    if (this.isModalInitialized && this.isReorderWorkflowSelected) {
      this.selectSystemDetailsOfReorder();
    }
    this.radioInputReset = null;
    this.cartWasSelectedInThisModal = false;
  }

  // Will be rework for BP

  /// Get system details of equipment for reorder
  /// If user is not BP get system details from install base
  selectSystemDetailsOfReorder(): void {
    if (this.isBusinessPartnerRole) {
      this.shsEquipmentFacade.getShsEquipment(this.flNumberOfReorder).pipe(
        take(1),
      ).subscribe(equipment => {
        this.systemDetailsOfReorder = EquipmentUtils.convertEquipmentToSystemDetails(equipment.data);
      });
    } else {
      this.catalogFacade.getShsEquipmentInstallBaseSystem(this.flNumberOfReorder).pipe(
        take(1),
      ).subscribe(shsEquipmentInstalledBase => {
        if (shsEquipmentInstalledBase?.data) {
          this.systemDetailsOfReorder =
            EquipmentUtils.convertEquipmentToSystemDetails(
              shsEquipmentInstalledBase.data?.find(shs => shs.attributes.siemensEquipmentId === this.flNumberOfReorder) ?? shsEquipmentInstalledBase.data[0],
            );
        }
      });
    }
  }

  close(): void {
    this.closeModal.emit();
  }

  /**
   * Sets the state of the modal and buttons
   * @param {boolean} isInProgress
   * @param {string} selectedOption
   */
  setModalState(isInProgress: boolean, selectedOption?: string): void {
    this.isAddToCartInProgress = isInProgress;
    this.isModalInProgress.emit(isInProgress);

    if (!selectedOption) {
      this.isAddToExistingCartBtn = isInProgress;
      this.isAddToNewCartBtn = isInProgress;
      return;
    }

    switch (selectedOption) {
      case ModalOptions.ExistingCart:
        this.isAddToExistingCartBtn = isInProgress;
        break;
      case ModalOptions.NewCart:
        this.isAddToNewCartBtn = isInProgress;
        break;
      default:
        break;
    }
  }

  selectEquipment(): void {
    this.router.navigate(['/my-installed-base'], {
      queryParams: {
        tab: EInstalledBaseTabs.EQUIPMENT,
      },
    }).then();
  }

  /**
   * Sets selected cart from list of carts
   * @param cart
   */
  selectCart(cart: ICart): void {
    this.cartSelected = cart;
    this.cartWasSelectedInThisModal = true;
  }

  /**
   * Sets selected cart from list of carts
   */
  selectCartsWithItems(): void {
    combineLatest([
      this.marketingFacade.selectCarts(),
      this.marketingFacade.selectCartsItems(),
      this.marketingFacade.selectCartItemsWithDetails(),
    ]).pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ([carts, cartsItems, cartItemsWithDetails]: [ICart[], ICartItemWithDetail[], ICartItemWithDetail[]]) => {
          this._populateCartsWithItems(carts, cartsItems, cartItemsWithDetails);
          this.isLoadingCarts = false;
        },
        error: () => {
          this.isLoadingCarts = false;
        },
      });
  }

  /**
   * Populates all carts with their respective items and concrete product details
   * @param {ICart[]} carts - List of carts to populate
   * @param {ICartItemWithDetail[]} cartsItems - All cart items including concrete products
   * @param {ICartItemWithDetail[]} cartItemsWithDetails - Cart items with full details
   */
  private _populateCartsWithItems(carts: ICart[], cartsItems: ICartItemWithDetail[], cartItemsWithDetails: ICartItemWithDetail[]): void {
    this.carts = structuredClone(carts) as ICartWithItems[];
    const items: ICartItemWithDetail[] = cartsItems?.filter(c => c.type === 'items');
    const concreteProducts: ICartItemWithDetail[] = cartsItems?.filter(c => c.type === 'concrete-products');
    this.carts.forEach(cart => {
      this._populateCartItems(cart, items, concreteProducts, cartItemsWithDetails);
    });
  }

  /**
   * Populates a single cart with its related items based on cart relationships
   * @param {ICartWithItems} cart - The cart to populate with items
   * @param {ICartItemWithDetail[]} items - Filtered items of type 'items'
   * @param {ICartItemWithDetail[]} concreteProducts - Filtered items of type 'concrete-products'
   * @param {ICartItemWithDetail[]} cartItemsWithDetails - Cart items with full details
   */
  private _populateCartItems(cart: ICartWithItems, items: ICartItemWithDetail[], concreteProducts: ICartItemWithDetail[], cartItemsWithDetails: ICartItemWithDetail[]): void {
    cart.items = [];
    if (cart.relationships) {
      cart.relationships.items?.data?.forEach((data: IBaseModel) => {
        this._addItemToCart(cart, data, items, concreteProducts, cartItemsWithDetails);
      });
    }
  }

  /**
   * Adds a single item to a cart, skipping configurable items
   * @param {ICartWithItems} cart - The cart to add the item to
   * @param {IBaseModel} data - The relationship data referencing the item
   * @param {ICartItemWithDetail[]} items - Filtered items of type 'items'
   * @param {ICartItemWithDetail[]} concreteProducts - Filtered items of type 'concrete-products'
   * @param {ICartItemWithDetail[]} cartItemsWithDetails - Cart items with full details
   */
  private _addItemToCart(cart: ICartWithItems, data: IBaseModel, items: ICartItemWithDetail[], concreteProducts: ICartItemWithDetail[], cartItemsWithDetails: ICartItemWithDetail[]): void {
    const relatedItem: ICartItemWithDetail | undefined = cartItemsWithDetails?.find(item => item.id === data.id) ?? items?.find(item => item.id === data.id);
    const hasConfigurableItems: boolean = !!relatedItem?.attributes.cpqConfigId;
    if (!hasConfigurableItems) {
      cart.items.push({
        details: concreteProducts.find(product => product.id === data.id),
        quantity: relatedItem?.attributes.quantity,
      });
    }
    cart.hasConfigurableItems = hasConfigurableItems;
  }

  private selectPricingGroupForConsumables(): void {
    this.catalogFacade.getPricingGroupForConsumables().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(pricingGroup => {
        this.pricingGroupForConsumables = pricingGroup;
      },
    );
  }

  isContractInCart(cart: ICartWithItems): boolean {
    return !!cart?.hasConfigurableItems;
  }

  /**
   * Checks if cart can be enabled for purchase for selected order/item
   * @param cart
   * @returns {boolean}
   */
  isCartAvailableForItem(cart: ICartWithItems): boolean {
    const selectedCartSystemDetails: ISystemDetails | undefined = cart.attributes?.systemDetails;
    const onlyConsumableProducts: boolean = this.checkIfOrderContainsOnlyConsumableItems();
    if (!this.isBusinessPartnerRole && selectedCartSystemDetails?.siemensEquipmentId) {
      return (onlyConsumableProducts && !selectedCartSystemDetails?.isSparePartsAvailable) ||
        selectedCartSystemDetails?.isSparePartsAvailable;
    }
    return true;
  }

  /**
   * Returns true if only consumable items are in `items`
   * @returns {boolean}
   */
  checkIfOrderContainsOnlyConsumableItems(): boolean {
    return !this.items?.some(item => item?.data?.sapP40MaterialPricingGroup3 !== this.pricingGroupForConsumables);
  }

  toggleTab(id: string): void {
    const index: number = this.tabsOpened.indexOf(id);
    if (index > -1) {
      this.tabsOpened.splice(index, 1);
    } else {
      this.tabsOpened.push(id);
    }
  }

  addItemsToCart(items: IAddItemModalItem[]): void {
    items.forEach((item, index) => {
      this.addToCart(item);

      if (index === items.length - 1) {
        this.closeModalAfterAddToCartOperation();
      }
    });
  }

  private addToCart(item: IAddItemModalItem): void {
    this.analyticsService.setProducts(item.data);
    this.analyticsService.trackCart('cart.add');
    this.marketingFacade.addProductToCart(item.data, item.isConcrete, item.quantity);
  }

  // Will be rework for BP
  addItemToExistingCart(): void {
    const systemDetails: ISystemDetails | undefined = this.cartSelected?.attributes.systemDetails;

    if (this._checkIfOrderWithoutEquipmentIsAllowed() && !systemDetails) {
      this.addItemsToCart(this.items);
      this.closeModalAfterAddToCartOperation();
      this.router.navigate(['/shop-cart']);

      return;
    }

    if (!this.isSapStore && !systemDetails) {
      if (this.cartSelected && this.cartSelected.id !== this.currentCartId) {
        this.marketingFacade.switchDefaultCart(this.cartSelected.id);
      }

      //workaround for non-SAP store
      this.setModalState(true, ModalOptions.ExistingCart);
      if (this.items.length > 1) {
        this.marketingFacade.reorderAllItems(this.items);
      } else {
        this.marketingFacade.reorderItem(this.items[0]);
      }

      if (this.isBusinessPartnerRole) {
        this.router.navigate(['/equipment-selection'], {
          queryParams: {
            orderId: this.orderId,
            purchaseActivityTab: this.purchaseActivityTab,
          },
        }).then();
      } else {
        this.router.navigate(['/my-installed-base'], {
          queryParams: {
            tab: EInstalledBaseTabs.EQUIPMENT,
            orderId: this.orderId,
            purchaseActivityTab: this.purchaseActivityTab,
          },
        }).then();
      }
    } else if (systemDetails) {
      this.addToCartWithSystemDetails(systemDetails);
      // TODO: For BP users also set sold-to from previous order
    } else if (this.isReorderWorkflowSelected) {
      this.addToCartWithoutSystemDetails();
    } else {
      this.showModalSelectEquipment = true;
    }
    this.cartWasSelectedInThisModal = false;
  }

  private addToCartWithSystemDetails(systemDetails: ISystemDetails): void {
    this.setModalState(true, ModalOptions.ExistingCart);
    this.loadSelectedCartAndAddItem(systemDetails);
  }

  private addToCartWithoutSystemDetails(): void {
    if (this.systemDetailsOfReorder) {
      this.setModalState(true, ModalOptions.ExistingCart);
      this.loadSelectedCartAndAddItem(null);
    }
  }

  private loadSelectedCartAndAddItem(cartSystemDetails: ISystemDetails | null): void {
    if (!this.cartSelected) {
      return;
    }
    let additionalAttributes: ICartUpdateRequestAttributes | undefined = {systemDetails: this.systemDetailsOfReorder};
    if (cartSystemDetails) {
      additionalAttributes = undefined;
    }
    if (this.cartSelected.id === this.currentCartId) {
      if (additionalAttributes) {
        // update cart with system details from original order
        this.marketingFacade.updateCartById(this.currentCartId, additionalAttributes);
      }
      this.marketingFacade.getCurrentCartItems().pipe(
        skipWhile(cart => !cart?.data?.attributes.systemDetails),
        take(1),
      ).subscribe({
        next: _ => this.afterCartIsLoaded(this.getItemsWithUpdatedSystemDetails()),
      });
    } else {
      this.marketingFacade.switchDefaultCart(this.cartSelected.id, additionalAttributes);
      this.marketingFacade.selectCartId().pipe(
        skipWhile(cartId => cartId != this.cartSelected?.id),
        take(1),
      ).subscribe({
        next: _ => this.afterCartIsLoaded(this.getItemsWithUpdatedSystemDetails()),
      });
    }
  }

  // Will be rework for BP
  private getItemsWithUpdatedSystemDetails(): IAddItemModalItem[] {
    return this.items.map(item => {
      return {
        ...item,
        data: {
          ...item.data,
        },
      };
    });
  }

  afterCartIsLoaded(items: IAddItemModalItem[]): void {
    this.marketingFacade.selectLoadingCartDataInProgress().pipe(
      take(1),
    ).subscribe(isLoading => {
      if (!isLoading) {
        if (this.cartSelected?.id !== this.currentCartId) {
          this.showNotificationYourCartWasChanged();
        }
        this.addItemsToCart(items);
      }
    });
  }

  private showNotificationYourCartWasChanged(): void {
    this.configurationFacade.appendNotification({
      type: 'success',
      title: 'shop-cart.your-cart-was-changed-title',
      messages: [{
        key: 'shop-cart.your-cart-was-changed-text',
      }],
      actions: [
        {
          type: '',
          label: 'shop-cart.close',
          css: ['button', 'button--secondary'],
        },
        {
          type: AppActions.redirectToShopCart.type,
          label: 'shop-cart.go-to-cart',
          css: ['button', 'button--primary'],
        },
      ],
    });
  }

  /**
   * Function is called to add item to new cart based on selected workflow
   */
  addItemToNewCart(): void {
    if (this._checkIfOrderWithoutEquipmentIsAllowed()) {
      this._addItemToNewCartWithoutEquipmentDetails();
      this.closeModalAfterAddToCartOperation();
      this.router.navigate(['/shop-cart']);

      return;
    }

    if (this.isReorderWorkflowSelected) {
      this.addItemToNewCartWithReorderWorkflow();
    } else {
      this.addItemToNewCartWithDefaultWorkflow();
    }
  }

  /**
   * Adds item to new cart without equipment details.
   * - Used when non-equipment purchase is allowed for all items in the order.
   * - Creates an empty cart.
   *
   * @private
   */
  private _addItemToNewCartWithoutEquipmentDetails(): void {
    this.marketingFacade.createEmptyCart();
    this.marketingFacade.selectCartId().pipe(
      skipWhile(id => !id || id === this.currentCartId),
      take(1),
    ).subscribe(() => {
      this.setModalState(true, ModalOptions.NewCart);
      this.addItemsToCart(this.items);
    });
  }

  /**
   * Adds item to new cart with reorder workflow
   * Navigate based on user role
   * @private
   */
  private addItemToNewCartWithReorderWorkflow(): void {
    this.setModalState(true, ModalOptions.NewCart);
    this.marketingFacade.createEmptyCart();
    this.marketingFacade.selectCartId().pipe(
      skipWhile(id => !id || id === this.currentCartId),
      take(1),
    ).subscribe(() => {
      if (this.items.length > 1) {
        this.marketingFacade.reorderAllItems(this.items);
      } else {
        this.marketingFacade.reorderItem(this.items[0]);
      }

      if (this.isBusinessPartnerRole) {
        this.router.navigate(['/equipment-selection'], {
          queryParams: {
            orderId: this.orderId,
            purchaseActivityTab: this.purchaseActivityTab,
          },
        }).then();
      } else {
        this.router.navigate(['/my-installed-base'], {
          queryParams: {
            tab: EInstalledBaseTabs.EQUIPMENT,
            orderId: this.orderId,
            purchaseActivityTab: this.purchaseActivityTab,
          },
        }).then();
      }
    });
  }

  /**
   * Adds item to new cart with default workflow
   * @private
   */
  private addItemToNewCartWithDefaultWorkflow(): void {
    this.marketingFacade.createEmptyCart();
    if (this.cartSelected?.attributes.systemDetails) {
      this.marketingFacade.selectCartId().pipe(
        skipWhile(id => !id || id === this.currentCartId),
        take(1),
      ).subscribe(() => {
        this.setModalState(true, ModalOptions.NewCart);
        this.addItemsToCart(this.items);
      });
    } else {
      this.showModalSelectEquipment = true;
    }
  }

  private closeModalAfterAddToCartOperation(): void {
    this.marketingFacade.isAddItemOperationInProgress().pipe(
      debounceTime(100),
      skipWhile(inProgress => inProgress === true),
      takeUntil(this.unsubscribe$),
    ).subscribe(() => {
      this.setModalState(false);
      this.close();
    });
  }

  hasSelectedCartSystemDetails(): boolean {
    if (this.cartSelected) {
      if (this.isBusinessPartnerRole || !this.isSapStore) {
        return true;
      }

      return (this.systemDetailsOfReorder && !this.cartSelected?.attributes.systemDetails)
        || !!this.cartSelected?.attributes.systemDetails;
    }
    return true;
  }

  /**
   * Function is called to determine state of button AddToCart active/disabled
   * @returns {boolean}
   */
  isReorderButtonDisabled(): boolean {
    return !this.cartWasSelectedInThisModal || this.isAddToCartInProgress || !this.currentCartId || !this.isSapP40Enable;
  }

  /**
   * Sets the state of the isBusinessPartnerRole
   * @private
   */
  private selectIsBPRole(): void {
    this.customerFacade.isBusinessPartner()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(isBPRole => {
        this.isBusinessPartnerRole = isBPRole;
      });
  }

  /**
   * Returns true if non-equipment purchase is allowed for all items in the order
   *
   * @returns {boolean}
   */
  private _checkIfOrderWithoutEquipmentIsAllowed(): boolean {
    if (!this.isNonEquipmentPurchaseEnabled) {
      return false;
    }

    return this.items.every(item =>
      item?.data?.productWebshopCategory &&
      this.supportedCategoriesForNonEquipmentPurchase.includes(item.data.productWebshopCategory),
    );
  }

  /**
   * Method for retrieving IsNonEquipmentPurchaseEnabled feature toggle value
   * If enabled, retrieves supported categories for non-equipment purchase
   *
   * @private
   */
  private _isNonEquipmentPurchaseEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.NON_EQUIPMENT_PURCHASE).pipe(
      tap((isEnabled: boolean) => {
        if (isEnabled) {
          this.configurationFacade.getNonEquipmentPurchaseSupportedCategories$().pipe(
            take(1),
          ).subscribe((categories: string[]) => {
            this.supportedCategoriesForNonEquipmentPurchase = categories;
          });
        }
        this.isNonEquipmentPurchaseEnabled = isEnabled;
      }),
      take(1),
    ).subscribe();
  }
}
